import { NaturalPersonDto } from "api/actor";
import { DUPLICATE_CONTACT_EMAIL_MESSAGE } from "features/new-actor/form/validation-rules/optim/legalEntityValidationRules";
import { GenericMessage } from "components/feedback/Message";
import { UsersGroupIcon } from "components/icons/customIcons";
import { Sizes } from "components/forms/NumberSelector";
import { PersonSearchBar } from "features/subscription-process/components/person-search/PersonSearchBar";
import { SearchPersonResult } from "components/domain/SearchBar/types";
import { CardsSection } from "components/layout/Section/CardsSection"
import { NsKey } from "enums/NsKey";
import { LegalEntityFormFieldNames } from "features/new-actor/enums/legalEntityFormFieldNames";
import { mapNaturalPerson } from "features/new-actor/mappers/optim/mapNaturalPerson";
import { LegalEntityFormValues } from "features/new-actor/types/optim/legalEntityFormValues";
import { NaturalPersonFormValues } from "features/new-actor/types/optim/naturalPersonFormValues";
import { selectReferentialData } from "features/referentials/selectors/selectReferentialDataByKey";
import { useFormikContext } from "formik";
import { generateGUID, NewGuidPrefix } from "helpers/generateGUID";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { useDialog } from "hooks/useDialog";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { DashedAddButton } from "components/ui/DashedAddButton";
import { UpiContactCard } from "./UpiContactCard";
import { UpiContactForm } from "./UpiContactForm";

interface UpiContactSectionProps {
  /** Narrow container (side panel): one contact per row, tighter cards. */
  compact?: boolean;
}

/* A duplicated email is the one contact error the generic "incomplete information" hint misleads
   on: every field is filled. Any other error keeps the generic hint (a bare "required" would not
   say which field). */
const duplicateEmailMessage = (contactError: unknown): string | undefined => {
  const email = (contactError as { email?: unknown } | undefined)?.email;
  return email === DUPLICATE_CONTACT_EMAIL_MESSAGE ? email : undefined;
};

export const UpiContactSection = ({ compact = false }: UpiContactSectionProps) => {
  const { openDialog } = useDialog()
  const { t } = useDynamicTranslation([NsKey.Common, NsKey.ActorForm])
  const referentialsData = useSelector(selectReferentialData)

  const { values, setFieldValue, errors } = useFormikContext<LegalEntityFormValues>()

  const currentContacts = useMemo(() => {
    return values.contacts
  }, [values])

  // Erreurs par contact (index aligné sur values.contacts). Formik met une string
  // sur le champ pour les erreurs de tableau (test has-gerant) : on ne garde que
  // la forme tableau, qui porte les erreurs de champs d'un contact précis.
  const contactErrors = useMemo(() => {
    const raw = errors[LegalEntityFormFieldNames.Contacts]
    return Array.isArray(raw) ? raw : []
  }, [errors])

  const isCurrentContactEmpty = useMemo(() => {
    if (currentContacts) {
      return currentContacts.length === 0
    }
    return true
  }, [currentContacts])

  const hasGerant = useMemo(() => {
    return currentContacts?.some(contact => contact.companyRole === 'GERANT') ?? false
  }, [currentContacts])

  const updateContact = useCallback((contactId: string, updatedContact: NaturalPersonFormValues) => {
    if (!currentContacts) {
      return
    }
    const entityIndex = currentContacts.findIndex(item => item.id === contactId);
    const newContacts = entityIndex !== -1
      ? currentContacts.map((entity, index) =>
        index === entityIndex ? { ...entity, ...updatedContact } : entity
      )
      : [...currentContacts, updatedContact];
    setFieldValue(LegalEntityFormFieldNames.Contacts, newContacts)
  }, [currentContacts, setFieldValue]);

  const addContact = useCallback((contact: NaturalPersonFormValues) => {
    contact.id = `${NewGuidPrefix}-${generateGUID()}`
    let newValues = [contact]
    if (currentContacts) {
      newValues = [
        ...currentContacts,
        contact
      ]
    }
    setFieldValue(LegalEntityFormFieldNames.Contacts, newValues)
  }, [currentContacts, setFieldValue])

  const deleteContact = useCallback((id: string) => {
    if (!currentContacts) {
      return
    }
    const newContacts = currentContacts.filter((contact) => contact.id !== id)
    setFieldValue(LegalEntityFormFieldNames.Contacts, newContacts)
  }, [currentContacts, setFieldValue])

  const onCreateContact = useCallback(() => {
    openDialog({
      title: t("ACTOR_FORM.LABELS.ADD_CONTACT_TITLE"),
      size: Sizes["2xl"],
    },
      <UpiContactForm handleSubmit={addContact} />
    )
  }, [openDialog, t, addContact])

  const onUpdateContact = useCallback((pendingContact: NaturalPersonFormValues) => {
    const id = pendingContact.id
    if (id) {
      openDialog({
        title: t("ACTOR_FORM.LABELS.EDIT_CONTACT_TITLE"),
        size: Sizes["2xl"],
      },
        <UpiContactForm handleSubmit={(contact) => updateContact(id, contact)} pendingContact={pendingContact} deleteContact={deleteContact} />
      )
    }
  }, [openDialog, t, updateContact, deleteContact])

  const onSelectContact = useCallback((person: SearchPersonResult | null) => {
    if (person) {
      let newValues = [mapNaturalPerson(person.data as NaturalPersonDto, referentialsData)]
      if (currentContacts) {
        newValues = [
          ...currentContacts,
          mapNaturalPerson(person.data as NaturalPersonDto, referentialsData)
        ]
      }
      setFieldValue(LegalEntityFormFieldNames.Contacts, newValues)
    }
  }, [currentContacts, referentialsData, setFieldValue])

  return <CardsSection
    icon={<UsersGroupIcon className="h-8 w-8 text-primary" />}
    title={t("ACTOR_FORM.LEGAL_ENTITY.LABELS.CONTACTS")}
    titleAside={
      <PersonSearchBar placeholder={t("COMMON.LABELS.SEARCH")} onSelect={onSelectContact} withLegalEntity={false} size='sm' />
    }
  >
    <div className="space-y-3">
      {!isCurrentContactEmpty && !hasGerant && (
        <GenericMessage
          type="warning"
          size="sm"
          message={t("ACTOR_FORM.LEGAL_ENTITY.LABELS.GERANT_REQUIRED")}
          dismissible={false}
        />
      )}
      <div className="flex items-stretch gap-4">
        {
          isCurrentContactEmpty &&
          <DashedAddButton label={t("ACTOR_FORM.LABELS.ADD_CONTACT_TITLE")} onClick={() => onCreateContact()} compact={compact} />
        }
        {
          !isCurrentContactEmpty &&
          <div className={`grid ${compact ? 'grid-cols-1 gap-3' : 'grid-cols-2 gap-4'} w-full`}>
            {
              currentContacts?.map((entity, index) => (
                <UpiContactCard
                  key={index}
                  entity={entity}
                  onUpdate={onUpdateContact}
                  onDelete={deleteContact}
                  hasError={Boolean(contactErrors[index])}
                  errorMessage={duplicateEmailMessage(contactErrors[index])}
                  compact={compact}
                />
              ))
            }

            <DashedAddButton label={t("ACTOR_FORM.LABELS.ADD_CONTACT_TITLE")} onClick={() => onCreateContact()} compact={compact} />
          </div>
        }
      </div>
    </div>
  </CardsSection>
}
